import { Switch, Route, type RouteProps, type DefaultParams, type PathPattern } from 'wouter';
import { Suspense } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTestingMode } from '@/hooks/useTestingMode';
import TopNavigation from '@/components/layout/top-navigation';
import UserRoleSelector from '@/components/testing/user-role-selector';
import { PageLoadingFallback } from '@/components/ui/loading-spinner';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { SkipLink } from '@/components/accessibility/skip-link';
import { AccessibilityMenu } from '@/components/accessibility/accessibility-menu';
import { CookieBanner } from '@/components/legal/cookie-banner';
import { ConsentGate } from '@/components/legal/consent-gate';
import { SignedOutFallback, AuthUnavailableFallback } from '@/components/auth/auth-gate-fallback';

// Lazy-loaded page components for code splitting.
// Wrapped with `lazyWithRetry` so a stale-chunk failure (e.g. the previous
// /patient-dashboard "Failed to fetch dynamically imported module" crash
// after a deploy) auto-recovers via a one-shot full reload instead of
// bubbling up to the ErrorBoundary.
const NotFound = lazyWithRetry(() => import('@/pages/not-found'), 'not-found');
const Landing = lazyWithRetry(() => import('@/pages/landing'), 'landing');
const PatientDashboard = lazyWithRetry(
  () => import('@/pages/patient-dashboard'),
  'patient-dashboard'
);
const PreviewDashboard = lazyWithRetry(
  () => import('@/pages/preview-dashboard'),
  'preview-dashboard'
);
const AdminDashboard = lazyWithRetry(() => import('@/pages/admin-dashboard'), 'admin-dashboard');
const ProviderDashboard = lazyWithRetry(
  () => import('@/pages/provider-dashboard'),
  'provider-dashboard'
);
const ProviderRegistration = lazyWithRetry(
  () => import('@/pages/provider-registration'),
  'provider-registration'
);
const ProviderVerification = lazyWithRetry(
  () => import('@/pages/provider-verification'),
  'provider-verification'
);
const DoctorDashboard = lazyWithRetry(() => import('@/pages/doctor-dashboard'), 'doctor-dashboard');
const TelemedicineDashboard = lazyWithRetry(
  () => import('@/pages/telemedicine-dashboard'),
  'telemedicine-dashboard'
);
const AppointmentsPage = lazyWithRetry(() => import('@/pages/appointments'), 'appointments');
const MedicationsPage = lazyWithRetry(() => import('@/pages/medications'), 'medications');
const MedicalRecordsPage = lazyWithRetry(
  () => import('@/pages/medical-records'),
  'medical-records'
);
const HealthMetricsPage = lazyWithRetry(() => import('@/pages/health-metrics'), 'health-metrics');
const NotificationsPage = lazyWithRetry(() => import('@/pages/notifications'), 'notifications');
const BookAppointment = lazyWithRetry(() => import('@/pages/book-appointment'), 'book-appointment');
const AddMedication = lazyWithRetry(() => import('@/pages/add-medication'), 'add-medication');
const EmergencyPage = lazyWithRetry(() => import('@/pages/emergency'), 'emergency');
const ProviderSearch = lazyWithRetry(() => import('@/pages/provider-search'), 'provider-search');
const PatientProfile = lazyWithRetry(() => import('@/pages/patient-profile'), 'patient-profile');
const DoctorProfile = lazyWithRetry(() => import('@/pages/doctor-profile'), 'doctor-profile');
const AdminProfile = lazyWithRetry(() => import('@/pages/admin-profile'), 'admin-profile');
const AdminMfa = lazyWithRetry(() => import('@/pages/admin-mfa'), 'admin-mfa');
const HospitalProfile = lazyWithRetry(() => import('@/pages/hospital-profile'), 'hospital-profile');
const PharmacyProfile = lazyWithRetry(() => import('@/pages/pharmacy-profile'), 'pharmacy-profile');
const HealthServiceProfile = lazyWithRetry(
  () => import('@/pages/health-service-profile'),
  'health-service-profile'
);
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/privacy-policy'), 'privacy-policy');
const TermsOfService = lazyWithRetry(() => import('@/pages/terms-of-service'), 'terms-of-service');
const PrivacyPage = lazyWithRetry(() => import('@/pages/privacy'), 'privacy');
const TermsPage = lazyWithRetry(() => import('@/pages/terms'), 'terms');
const HelpPage = lazyWithRetry(() => import('@/pages/help'), 'help');
const ContactPage = lazyWithRetry(() => import('@/pages/contact'), 'contact');
const CookiePolicy = lazyWithRetry(() => import('@/pages/cookie-policy'), 'cookie-policy');
const MedicalDisclaimerPage = lazyWithRetry(
  () => import('@/pages/medical-disclaimer'),
  'medical-disclaimer'
);
const SecuritySettingsPage = lazyWithRetry(
  () => import('@/pages/security-settings'),
  'security-settings'
);
const SettingsPage = lazyWithRetry(() => import('@/pages/settings'), 'settings');
const MessagesPage = lazyWithRetry(() => import('@/pages/messages'), 'messages');
const AIAssistant = lazyWithRetry(() => import('@/pages/ai-assistant'), 'ai-assistant');
const SymptomChecker = lazyWithRetry(() => import('@/pages/symptom-checker'), 'symptom-checker');
const Onboarding = lazyWithRetry(() => import('@/pages/onboarding'), 'onboarding');
const ReviewsPage = lazyWithRetry(() => import('@/pages/reviews'), 'reviews');
const SocialFeed = lazyWithRetry(() => import('@/pages/social-feed'), 'social-feed');
const PrescriptionsPage = lazyWithRetry(() => import('@/pages/prescriptions'), 'prescriptions');
const IntegrationsPage = lazyWithRetry(() => import('@/pages/integrations'), 'integrations');
const CreatePrescriptionPage = lazyWithRetry(
  () => import('@/pages/create-prescription'),
  'create-prescription'
);
const LabResultsPage = lazyWithRetry(() => import('@/pages/lab-results'), 'lab-results');
const UploadLabResultPage = lazyWithRetry(
  () => import('@/pages/upload-lab-result'),
  'upload-lab-result'
);
const PaymentsPage = lazyWithRetry(() => import('@/pages/payments'), 'payments');
const InsurancePage = lazyWithRetry(() => import('@/pages/insurance'), 'insurance');
const RegisterPage = lazyWithRetry(() => import('@/pages/register'), 'register');
const LoginPage = lazyWithRetry(() => import('@/pages/login'), 'login');

// Lazy-load floating chat button (rendered after auth check)
const FloatingChatButton = lazyWithRetry(
  () => import('@/components/ai-assistant/floating-chat-button'),
  'floating-chat-button'
);

/**
 * Makes route MATCHING auth-independent (spec 028-h1-route-404, plan D-3/D-4):
 * a `GatedRoute` always matches its `path` the same way a bare `<Route>`
 * would, regardless of auth state, and only the rendered COMPONENT varies —
 * `resolution === 'authenticated'` is the only case that renders the real
 * page. This is what stops a signed-out visitor at a real, gated URL from
 * ever reaching the terminal `NotFound` (the QA H1 Half B defect): the route
 * still matches, so `NotFound` — which lives after every other route in the
 * `Switch` — never gets a turn.
 *
 * DISPLAY GATE, NOT AN AUTHORIZATION BOUNDARY: every path this wraps is still
 * public, and the withheld page component is one breakpoint away from being
 * visible regardless. The actual control is server-side — `loadOwned*`
 * ownership checks and the 404-not-403 invariant on every API route
 * (`CLAUDE.md` > Security posture). Nothing here should ever be treated as
 * sufficient on its own.
 *
 * Declared at module scope, not inside `Router`, so its identity is stable
 * across renders and its subtree does not remount on every parent re-render.
 *
 * Does NOT forward `Switch`'s injected `match` prop: wouter's `RouteProps`
 * does not declare it, and the inner `<Route>` re-derives its own match when
 * the prop is absent — one redundant `matchRoute` call on the single matched
 * route, same result.
 */
export function GatedRoute<
  T extends DefaultParams | undefined = undefined,
  RoutePath extends PathPattern = PathPattern,
>(props: RouteProps<T, RoutePath>) {
  const { resolution, retryAuth } = useAuth();
  const { path, component: Component } = props;

  if (resolution === 'authenticated' && Component) {
    return <Route path={path} component={Component} />;
  }
  if (resolution === 'unreachable') {
    return <Route path={path} component={() => <AuthUnavailableFallback onRetry={retryAuth} />} />;
  }
  if (resolution === 'pending') {
    return <Route path={path} component={PageLoadingFallback} />;
  }
  // 'refused', or any unmatched/undefined value (plan risk R-5 — e.g. a
  // testing-mode branch that forgot to set `resolution`): fail closed. A
  // gated page renders only on an EXPLICIT 'authenticated'.
  return <Route path={path} component={SignedOutFallback} />;
}

/**
 * `/` is public with auth-varying content, so it is deliberately NOT a
 * `GatedRoute`: a signed-out visitor sees `Landing`, not a sign-in prompt.
 *
 * Rendered via a real Route element with path="/" INSIDE the Switch — never
 * as a bare pathless `<HomeRoute />` child. `Switch` selects the first child
 * whose `props.path` matches, and a child with NO `path` prop is a catch-all
 * (that is how the terminal `NotFound` route works). A pathless
 * `<HomeRoute />` therefore swallowed every location, and since the old
 * inner Route it wrapped only rendered content at `/`, every route listed
 * after it — all ~35 GatedRoutes and NotFound — silently rendered NOTHING
 * (the live blank-page incident of 2026-07-29). This component now renders
 * the chosen page directly; path matching belongs to the Switch's own Route
 * element.
 *
 * Module scope keeps its identity stable across `Router` re-renders so the
 * subtree does not remount.
 */
export function HomeRoute(): JSX.Element {
  const { resolution, retryAuth, user } = useAuth();

  if (resolution === 'unreachable') {
    return <AuthUnavailableFallback onRetry={retryAuth} />;
  }
  if (resolution === 'authenticated') {
    const userType = user?.userType;
    if (userType === 'patient') {
      return <PatientDashboard />;
    }
    if (userType === 'system_admin') {
      return <AdminDashboard />;
    }
    if (
      userType !== undefined &&
      ['doctor', 'hospital', 'pharmacy', 'lab', 'health_services'].includes(userType)
    ) {
      return <ProviderDashboard />;
    }
    return <PatientDashboard />; // Default to patient dashboard
  }
  // 'pending', 'refused', or any unmatched value: the public landing page
  // is always a safe default at `/` — unlike a gated route, there is no
  // page here that must be withheld from a signed-out visitor.
  return <Landing />;
}

function Router(): JSX.Element {
  const { isAuthenticated, resolution } = useAuth();
  const { isTestingMode, switchTestRole, currentTestRole } = useTestingMode();

  if (resolution === 'pending') {
    return <PageLoadingFallback />;
  }

  return (
    <div>
      {/* Skip-to-content link — visible on keyboard focus (WCAG 2.4.1) */}
      <SkipLink />

      {/* Testing Mode User Role Selector */}
      {isTestingMode && (
        <UserRoleSelector currentRole={currentTestRole} onRoleChange={switchTestRole} />
      )}

      {/* md, not lg: TopNavigation's own responsive internals switch at md,
          so a lg gate strands its hamburger (md:hidden can never show inside
          hidden lg:block) and leaves 768-1023px with no nav chrome at all —
          below md the bottom nav's More panel carries logout + language
          instead (QA 2026-07-31 C3). */}
      {isAuthenticated && (
        <div className="hidden md:block">
          <TopNavigation />
        </div>
      )}

      <main id="main-content">
        <Suspense fallback={<PageLoadingFallback />}>
          <Switch>
            {/* Public routes - accessible to everyone */}
            <Route path="/register" component={RegisterPage} />
            <Route path="/login" component={LoginPage} />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            <Route path="/terms-of-service" component={TermsOfService} />
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/cookie-policy" component={CookiePolicy} />
            <Route path="/medical-disclaimer" component={MedicalDisclaimerPage} />
            <Route path="/help" component={HelpPage} />
            <Route path="/contact" component={ContactPage} />
            {/* Provider sign-up is a public entry point — the "Join as Provider"
                call to action on the landing page is only ever clicked by a
                signed-out visitor, so gating this route behind auth made the
                button lead nowhere. */}
            <Route path="/provider-registration" component={ProviderRegistration} />
            {/* Dashboard routes outside auth gate so component handles its own auth display */}
            <Route path="/provider-dashboard" component={ProviderDashboard} />
            <Route path="/patient-dashboard" component={PatientDashboard} />
            <Route path="/admin-dashboard" component={AdminDashboard} />
            <Route path="/doctor-dashboard" component={DoctorDashboard} />

            {/* `/` is public with auth-varying content — see HomeRoute's
                header comment for why it is not a GatedRoute, and why the
                `path` MUST live here on a real Route element: a pathless
                child is a catch-all to Switch and starves every route
                below it. */}
            <Route path="/" component={HomeRoute} />
            {/* Auth-gated routes. Route MATCHING is auth-independent (plan
                D-3/D-4): every path below matches the same way regardless of
                sign-in state, so a genuine 404 stays a genuine 404 and a
                signed-out visitor at a real path never reaches NotFound
                (QA H1, Half B). Only the rendered component depends on
                auth — see GatedRoute's header comment. */}
            <GatedRoute path="/provider-verification" component={ProviderVerification} />
            <GatedRoute path="/telemedicine" component={TelemedicineDashboard} />
            <GatedRoute path="/preview/:userType" component={PreviewDashboard} />
            {/* Feature-specific dedicated pages */}
            <GatedRoute path="/appointments" component={AppointmentsPage} />
            <GatedRoute path="/appointments/book" component={BookAppointment} />
            <GatedRoute path="/medications" component={MedicationsPage} />
            <GatedRoute path="/medications/add" component={AddMedication} />
            <GatedRoute path="/emergency" component={EmergencyPage} />
            <GatedRoute path="/records" component={MedicalRecordsPage} />
            <GatedRoute path="/health-metrics" component={HealthMetricsPage} />
            <GatedRoute path="/providers" component={ProviderSearch} />
            <GatedRoute path="/providers/search" component={ProviderSearch} />
            <GatedRoute path="/notifications" component={NotificationsPage} />
            {/* Profile pages */}
            <GatedRoute path="/profile" component={PatientProfile} />
            <GatedRoute path="/patient/profile" component={PatientProfile} />
            <GatedRoute path="/patient-profile" component={PatientProfile} />
            <GatedRoute path="/doctor-profile" component={DoctorProfile} />
            <GatedRoute path="/admin-profile" component={AdminProfile} />
            <GatedRoute path="/admin/mfa" component={AdminMfa} />
            <GatedRoute path="/hospital-profile" component={HospitalProfile} />
            <GatedRoute path="/pharmacy-profile" component={PharmacyProfile} />
            <GatedRoute path="/health-service-profile" component={HealthServiceProfile} />
            {/* New Feature Pages - Phase 4 */}
            <GatedRoute path="/ai-assistant" component={AIAssistant} />
            <GatedRoute path="/symptom-checker" component={SymptomChecker} />
            <GatedRoute path="/onboarding" component={Onboarding} />
            <GatedRoute path="/reviews" component={ReviewsPage} />
            <GatedRoute path="/social-feed" component={SocialFeed} />
            {/* Security Settings - Phase 5 */}
            <GatedRoute path="/settings/security" component={SecuritySettingsPage} />
            {/* Settings hub + secure messaging */}
            <GatedRoute path="/settings" component={SettingsPage} />
            <GatedRoute path="/messages" component={MessagesPage} />
            {/* E-Prescriptions */}
            <GatedRoute path="/prescriptions/create" component={CreatePrescriptionPage} />
            <GatedRoute path="/prescriptions" component={PrescriptionsPage} />
            {/* Lab Results */}
            <GatedRoute path="/lab-results/upload" component={UploadLabResultPage} />
            <GatedRoute path="/lab-results" component={LabResultsPage} />
            {/* Payments & Insurance — Task #16 */}
            <GatedRoute path="/payments" component={PaymentsPage} />
            <GatedRoute path="/insurance" component={InsurancePage} />
            {/* Healthcare Data Integrations (Task #19) */}
            <GatedRoute path="/integrations" component={IntegrationsPage} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </main>

      {/* Floating AI Assistant Button - visible when authenticated */}
      {isAuthenticated && (
        <Suspense fallback={null}>
          <FloatingChatButton />
        </Suspense>
      )}

      {/* Floating accessibility preferences menu — always available */}
      <AccessibilityMenu />

      {/* GDPR-style cookie banner — first visit only */}
      <CookieBanner />

      {/* First-sign-in / version-bump consent enforcement (no-op until auth) */}
      <ConsentGate />
    </div>
  );
}

function App(): JSX.Element {
  return <Router />;
}

export default App;
